// Deterministic entity-type → color mapping for the KG visualization.
//
// Generic, schema-agnostic. Every entity type — known or unknown, from any
// pipeline (Intel, Burro, future customers) — gets a stable color derived
// from its name. No hardcoded customer-specific lists.
//
// Design:
//   1. Hash the lowercased entity-type name (Java-style 31-multiplier hash —
//      good distribution, ~no clustering for short strings).
//   2. Pick a hue using golden-angle rotation: hue = (hash * 137.508°) mod 360.
//      The golden angle is the irrational hue spacing that maximises distance
//      between *any* N consecutive samples — so adjacent types in the schema
//      always look distinct, regardless of how many entity types there are.
//   3. Fixed saturation + lightness tuned for the dark KG canvas. High enough
//      to read, low enough to avoid eye strain.
//   4. Same string always maps to the same hue ⇒ stable across pipelines,
//      reloads, and surfaces.
//   5. Never returns grey / null.

/** Golden angle in degrees — the irrational hue step that yields maximally
 *  distinct colors for any number of samples (used by phyllotaxis, Munsell
 *  color generators, and `chroma.js`'s `cubehelix` palette). */
const goldenAngleDeg = 137.508;

/** Saturation/lightness tuned for legibility on the dark KG canvas. Bright
 *  enough to read at the small node-dot size, not so saturated that adjacent
 *  hues bleed into each other. */
const SATURATION = 0.78;
const LIGHTNESS = 0.6;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Convert HSL (h: 0-360, s: 0-1, l: 0-1) → `#rrggbb`. Inlined to keep this
 *  module dependency-free. */
function hslToHex(h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const channel = (n: number): string => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
}

/** Return a stable color for a given entity type. Same input ⇒ same color,
 *  always. Never returns grey / null. */
export function colorForEntityType(entityType: string): string {
  const key = (entityType || '').toLowerCase().trim();
  const hash = hashString(key);
  const hue = (hash * goldenAngleDeg) % 360;
  return hslToHex(hue, SATURATION, LIGHTNESS);
}
